import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { catchError, delayWhen, from, mergeMap, Observable, tap, throwError } from 'rxjs';

import {
    _BaasTransportHelper,
    _BarAppInfoManager,
    _BarXdc,
    _checkCompatibleOrigin,
    _OopsDomService,
    _OopsType,
    BaasAppState,
    BaasAuditType,
    BaasBootstrapOptions,
    BaasLoggingService,
    BaasMeetpuntService,
    BarStopwatch,
    checkUrlAbsolute,
    ensureGlobal,
    extractUrlParts
} from '@lvflanders/bar';

import { BAR_CORE_CONSTANTS } from '../constants/bar-core.constants';
import { BAR_LOCALHOST } from '../constants/tokens.constants';
import { BarHttpParams } from '../objects/bar-http-params';

@Injectable()
export class BarCoreInterceptor implements HttpInterceptor {
    readonly #state = inject(BaasAppState);
    readonly #appInfo = inject(_BarAppInfoManager);
    readonly #xdc = inject(_BarXdc);
    readonly #options = inject(BaasBootstrapOptions);
    readonly #meetpunt = inject(BaasMeetpuntService);
    readonly #logging = inject(BaasLoggingService);
    readonly #localhost = inject<boolean>(BAR_LOCALHOST);
    readonly #oopsDomService = inject(_OopsDomService);
    readonly #transportHelper = inject(_BaasTransportHelper);

    constructor() {
        this.#defineGlobal();
    }

    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get #handleAuthFailedStatusCombined(): boolean {
        // we handle it if defined globally through configuration and if the user didn't turn it off (if we don't have the configuration yet we assume we want to handle: init call)
        return this.#handleAuthFailedStatus && (!this.#state.config || this.#state.config.interception?.handleAuthFailed);
    }

    // gets if we handle the forbidden status based on a global linked to the local storage
    get #handleAuthFailedStatus(): boolean {
        let result = true;
        const tmp = window.localStorage.getItem('lvflanders.bar.ng.core.interception.handleAuthFailed');
        if (tmp != null && tmp.length > 0) {
            result = JSON.parse(tmp);
        }
        return result;
    }

    // sets if we handle the forbidden status based on a global linked to the local storage
    set #handleAuthFailedStatus(value: boolean) {
        window.localStorage.setItem('lvflanders.bar.ng.core.interception.handleAuthFailed', JSON.stringify(value));
    }

    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get #handleForbiddenStatusCombined(): boolean {
        // we handle it if defined globally through configuration and if the user didn't turn it off
        return this.#handleForbiddenStatus && this.#state.config?.interception?.handleForbidden;
    }

    // gets if we handle the forbidden status based on a global linked to the local storage
    get #handleForbiddenStatus(): boolean {
        let result = true;
        const tmp = window.localStorage.getItem('lvflanders.bar.ng.core.interception.handleForbidden');
        if (tmp != null && tmp.length > 0) {
            result = JSON.parse(tmp);
        }
        return result;
    }

    // sets if we handle the forbidden status based on a global linked to the local storage
    set #handleForbiddenStatus(value: boolean) {
        window.localStorage.setItem('lvflanders.bar.ng.core.interception.handleForbidden', JSON.stringify(value));
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const ommitInterceptor = request.params instanceof BarHttpParams && (<BarHttpParams>request.params).ommitInterceptor;
        if (ommitInterceptor) {
            return next.handle(request);
        } else {
            let stopwatch = null;
            // first we need to know if this is a background request, we don't want audit for audit (recursive)
            const backgroundRequest = request.params instanceof BarHttpParams && (<BarHttpParams>request.params).backgroundRequest;

            if (!backgroundRequest && this.#meetpunt.loggingEnabled) {
                stopwatch = new BarStopwatch();
                stopwatch.start();
            }
            // determine whether this is a domain request
            const domainRequest = this.#checkDomainRequest(request.url);
            const crossDomainRequest = this.#checkCrossDomainRequest(request.url);

            let promise: Promise<void> = null;
            // for domain requests (within same application) or cross domain request, we want to persist the basiscontext or at least sync the cookies if enabled
            if (domainRequest || crossDomainRequest) {
                if (this.#state.basiscontext != null) {
                    // before we do the call we make sure the cookie is up to date for the request
                    promise = this.#appInfo.persistBasiscontext({
                        caller: 'Interceptor',
                        url: request.url
                    });
                } else if (crossDomainRequest && this.#xdc.enabled) {
                    promise = this.#xdc.sync({
                        caller: 'Interceptor',
                        url: request.url
                    });
                }

                // clone request
                request = request.clone({
                    // needed for localhost cookie sharing
                    withCredentials: true,
                    headers: this.#defineHeaders(request.headers, domainRequest, crossDomainRequest)
                });
            }

            if (promise != null) {
                // execute promise before proceeding
                // use merge map to be able
                return from(promise).pipe(
                    mergeMap(() => {
                        return this.#handleRequest(request, next, stopwatch, domainRequest, crossDomainRequest);
                    })
                );
            } else {
                // no promise just proceed
                return this.#handleRequest(request, next, stopwatch, domainRequest, crossDomainRequest);
            }
        }
    }

    #handleRequest(
        request: HttpRequest<any>,
        next: HttpHandler,
        stopwatch: BarStopwatch,
        domainRequest: boolean,
        crossDomainRequest: boolean
    ): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // 455 custom code authentication engine or authentication handler (in localhost we also want redirect behavior)
                if (this.#handleAuthFailedStatusCombined && !this.#localhost && error.status == 455) {
                    this.#handleAuthFailed(error);
                }
                // 403 forbidden (e.g. actie allowed failed) handling, in localhost we don't want to get kicked out of application
                else if (this.#handleForbiddenStatusCombined && !this.#localhost && error.status == 403) {
                    this.#handleForbidden(error);
                }
                return throwError(() => error);
            }),
            // tap to execute some non asynchronous code after the request
            tap((ev: HttpEvent<any>) => {
                if (ev instanceof HttpResponse) {
                    if (domainRequest && this.#options.auth?.syncBasiscontextAfterRequestCompletion) {
                        // get the basiscontext based on cookie and update basiscontext in temp state and page state
                        // a request can lead to changed basiscontext
                        const basiscontext = this.#appInfo.getBasiscontext();
                        const basiscontextKey: keyof BaasAppState = 'basiscontext';
                        this.#state[<any>basiscontextKey] = basiscontext;
                    }
                    if (stopwatch != null) {
                        // read audit id response headers
                        const auditId = ev.headers.get('X-Audit-Id');
                        stopwatch.stop();
                        this.#meetpunt.log({
                            meetpunt: 'INTERCEPTOR',
                            meetpuntDetail: extractUrlParts(request.url).pathname,
                            waarde: stopwatch.duration,
                            meetpuntInfo: request.url,
                            auditId: auditId
                        });
                    }
                }
            }),
            // https://stackoverflow.com/a/52206312/2174621
            // here we execute another observable, but we don't alter the output which is needed by the return parameter of this function (delayWhen doesn't alter result)
            delayWhen((ev) => {
                // execute the sync promise in case it's a httpresponse (otherwise auto resolve)
                return from(
                    ev instanceof HttpResponse && crossDomainRequest
                        ? this.#xdc.sync({
                              caller: 'Interceptor-End',
                              url: request.url
                          })
                        : Promise.resolve()
                );
            })
        );
    }

    #defineHeaders(headers: HttpHeaders, domainRequest: boolean, crossDomainRequest: boolean): HttpHeaders {
        // needed for aspnet core knowing about ajax requests
        headers = headers.set('X-Requested-With', 'XMLHttpRequest');

        let requestToken = null;

        if (domainRequest && !crossDomainRequest && this.#state.localRequestToken != null && this.#xdc.enabled) {
            // local request cookie in case domain request && cross domain cookeis are enabled
            requestToken = this.#state.localRequestToken;
        } else if (this.#state.requestToken != null) {
            requestToken = this.#state.requestToken;
        }

        if (requestToken != null) {
            // antiforgery header
            headers = headers.set('X-XSRF-TOKEN', requestToken);
        }

        let auditId = null;
        let auditType = null;

        if (this.#state.routeId != null) {
            auditId = this.#state.routeId;
            auditType = BaasAuditType.Route;
        } else if (this.#state.pageId != null) {
            auditId = this.#state.pageId;
            auditType = BaasAuditType.Page;
        } else if (this.#state.sessionId != null) {
            auditId = this.#state.sessionId;
            auditType = BaasAuditType.Session;
        }

        if (auditId != null) {
            // set audit id so that audit system can chain everything together
            headers = headers.set('X-Audit-Id', auditId).set('X-Audit-Type', auditType);
        }

        //wimfra: adds baas header to request, when available in sessiondata.
        //this header will have preference to cookies, when being passed
        headers = this.#addBaasHeaders(headers);
        return headers;
    }

    #assignHeaders(headers: HttpHeaders, headersToAssign: Record<string, string>): HttpHeaders {
        for (const [key, value] of Object.entries(headersToAssign)) {
            if (!headers.has(key)) headers = headers.set(key, value);
        }
        return headers;
    }

    /**pass headers from session storage to call */
    #addBaasHeaders(headers: HttpHeaders): HttpHeaders {
        const baasHeaders = this.#transportHelper.generateBaasInterceptionHeaders();
        return this.#assignHeaders(headers, baasHeaders);
    }

    #checkDomainRequest(url: string): boolean {
        return (
            !checkUrlAbsolute(url) ||
            url.indexOf('localhost') > -1 ||
            url.startsWith(document.location.origin) ||
            _checkCompatibleOrigin(url, this.#options.compatibleOrigins)
        );
    }

    /* check if this is a cross domain request
        rules:
        - xdc must be enabled (localhost, not production...)
        - we must be doing a call to the release domain (which is not localhost)
    */
    #checkCrossDomainRequest(url: string): boolean {
        const result = false;
        if (this.#localhost) {
            // get the parts of the url being invoked by http
            const invokedUrlParts = extractUrlParts(url);
            // do the check if this is really a cross domain check (http call to origin of cross domain && cross domain is not localhost)
            return invokedUrlParts.origin == this.#state.location.release.origin && this.#state.location.release.hostname != 'localhost';
        }
        return result;
    }

    #handleAuthFailed(error: HttpErrorResponse): void {
        this.#performExtraLogging('Auth Failed (455)', error.url, 'Performing log off.');
        this.#oopsDomService.showOops({ type: _OopsType.Authentication });
    }

    #handleForbidden(error: HttpErrorResponse): void {
        this.#performExtraLogging('Forbidden (403)', error.url, 'Navigating to insufficient privileges page.');
        window.location.href = this.#state.location.baasStatic.url + BAR_CORE_CONSTANTS.PAGES.INSUFFICIENT_PRIVILEGES;
    }

    /** Define global variables related to interception */
    #defineGlobal(): void {
        // make sure the state is available globally for non Angular code
        const interception = ensureGlobal('lvflanders.bar.ng.core.interception');
        // here we define a global property to be used to turn on/off the fact that we handle auth failed responses
        Object.defineProperty(interception, 'handleAuthFailed', {
            set: (value: boolean) => {
                this.#handleAuthFailedStatus = value;
            },
            get: () => {
                return this.#handleAuthFailedStatus;
            },
            configurable: true
        });
        // here we define a global property to be used to turn on/off the fact that we handle auth failed responses
        Object.defineProperty(interception, 'handleForbidden', {
            set: (value: boolean) => {
                this.#handleForbiddenStatus = value;
            },
            get: () => {
                return this.#handleForbiddenStatus;
            },
            configurable: true
        });
    }

    #performExtraLogging(action: string, url: string, comment: string): void {
        let message = `[BarCoreInterceptor] ${action} - ${url}`;
        if (comment) message += ` - ${comment}`;
        if (this.#state?.basiscontext) message += ` - context: ${JSON.stringify(this.#state?.basiscontext)}`;
        this.#logging.warn({ message });
    }
}
